/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import DS from 'ember-data';
import { task } from 'ember-concurrency';
import { queryEncode } from 'cio/helpers/query';
import CampaignModel, { CampaignType } from 'cio/models/campaign';
import { isNone, isEmpty } from '@ember/utils';
import serializeEvent from 'cio/utils/conditions/serialize/event';
import EventCondition from 'cio/app-models/conditions/event';
import AttributeCondition from 'cio/app-models/conditions/attribute';
import serializeFilters from 'cio/utils/conditions/serialize/filter';
import { SerializedLegacyMessageFilter } from 'cio/types/conditions/legacy-filter';

interface MatchCountArgs
  extends Pick<
    CampaignModel,
    | 'type'
    | 'anchors'
    | 'objectAttributeTriggers'
    | 'relationshipAttributeTriggers'
    | 'dateTriggeredAttribute'
    | 'event'
    | 'attributeFilters'
  > {
  summary: string;
}

export default class MatchCount extends Component<MatchCountArgs> {
  @service store!: DS.Store;

  @tracked count: number | null = null;
  @tracked unableToGetCount = false;
  @tracked lastCalledFilters = '';

  readonly types = CampaignType;

  constructor(owner: unknown, args: MatchCountArgs) {
    super(owner, args);
    this.fetchCount.perform();
  }

  get hasSummary() {
    return !isEmpty(this.args.summary);
  }

  fetchCount = task(async () => {
    this.unableToGetCount = false;

    if (!this.hasSummary) {
      this.count = null;
      return;
    }

    // filters logic below, used here only for "form" type, which requires extra API call
    let filters: any;
    switch (this.args.type) {
      case CampaignType.Behavioral: {
        filters = serializeFilters(this.args.anchors, 'and', true);
        break;
      }
      case CampaignType.Date: {
        if (!isEmpty(this.args.dateTriggeredAttribute)) {
          filters = serializeFilters(
            [
              AttributeCondition.create({
                field: this.args.dateTriggeredAttribute,
                operator: 'exists',
              }),
            ],
            'and',
            true,
          );
        }
        break;
      }
      case CampaignType.Transactional: {
        if (!isEmpty(this.args.event)) {
          filters = serializeFilters(
            [
              {
                ...serializeEvent(
                  EventCondition.create({
                    name: this.args.event,
                  }),
                ),
                type: 'message',
              } as SerializedLegacyMessageFilter,
            ],
            'and',
            true,
          );
        }
        break;
      }
      case CampaignType.Form: {
        // forms submits are saved in segments, so need to find the correct segment if any
        let { attributeFilters } = this.args;
        let usedFormIds = new Set();
        (attributeFilters as any).children?.forEach((groupCondition: any) => {
          let formCondition = groupCondition.children?.[0];
          if (formCondition != null && formCondition.name === 'form_id' && !isEmpty(formCondition.value)) {
            usedFormIds.add(formCondition.value);
          }
        });
        let usedFormSegments: any[] = [];
        let segments = this.store.peekAll('segment').toArray();
        if (usedFormIds.size > 0) {
          if (segments.length === 0) {
            segments = (await this.store.findAll('segment')).toArray();
          }
          usedFormSegments = segments.filter(({ linkedFormId }) => usedFormIds.has(linkedFormId));
        }
        if (usedFormSegments?.length === usedFormIds.size) {
          filters = serializeFilters(
            [
              {
                type: 'segments_negatable',
                segments: usedFormSegments.map(({ id }) => id),
              },
            ] as any,
            'and',
            true,
          );
        } else {
          this.unableToGetCount = true;
        }
        break;
      }
      case CampaignType.Object:
      case CampaignType.ObjectRelationship: {
        // Currently backend does not support ['object_attribute','relationship_attribute'] filters for cutomers API
        // https://github.com/customerio/ui/pull/7946
        this.unableToGetCount = true;
      }
    }
    filters = isNone(filters) ? filters : queryEncode(filters);
    // filters section ends here

    this.lastCalledFilters = filters;
    try {
      let result = await this.store.query('customer', { filters });
      this.count = (result as any).meta?.pagination?.total ?? null;
      if (this.count == null) {
        this.unableToGetCount = true;
      }
    } catch (err) {
      // TODO: remove try/catch after "relationship_attribute" filter is supported on the API. https://github.com/customerio/services/pull/9047
      this.unableToGetCount = true;
    }
  });
}
